/**
 * Input plumbing for the kit: keys and menu actions to the dialog or the editor, arrows (with the
 * app's key repeat), steppers that repeat while held, and middle-button panning.
 */

import { ACTIONS } from '../../platform/options';
import type { KeyInput, MenuAction } from '../../ui/scene';
import { scrollBy } from '../model/view';
import type { KitApi } from './api';
import { FORM_ITEMS } from './formItems';
import type { ArrowDir } from './keys';
import { kitKey } from './keys';
import { inside, VIEW } from './layout';
import type { Modal } from './modals/modal';
import type { Zone } from './widgets';

const HOLD_DELAY_MS = 380;
const FAST_AFTER_MS = 1500;

const ACTION_KEYS: Partial<Record<MenuAction, string>> = {
  up: 'ArrowUp', down: 'ArrowDown', left: 'ArrowLeft', right: 'ArrowRight', confirm: 'Enter', back: 'Escape',
};
const ARROW_ACTION: Readonly<Record<string, MenuAction>> = {
  ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
};

export class KitInput {
  private shift = false;
  private held: { zone: Zone; ms: number; wait: number } | null = null;
  private panAt: { x: number; y: number } | null = null;

  constructor(private readonly k: KitApi) {}

  /** Big steps: Shift was held with the last key, or a stepper has been held down a while. */
  big(): boolean {
    return this.shift || (this.held !== null && this.held.ms > FAST_AFTER_MS);
  }

  /** Whether the app turns this arrow key into a repeating direction action (it is bound to one). */
  private repeats(code: string): boolean {
    const keys = this.k.host.services.options.get().keys;
    const action = ACTIONS.find((a) => keys[a].includes(code));
    return action === ARROW_ACTION[code];
  }

  key(key: KeyInput, modal: Modal | null): boolean {
    this.shift = key.shift;
    // Ctrl+V is left to the browser, whose paste event brings the text (`KitScene.onPaste`).
    if (key.ctrl && key.code === 'KeyV') return false;
    if (modal) {
      modal.key(key);
      return true;
    }
    // Each press is its own undo step; holding a key (or typing a word) merges.
    if (!this.k.editing) this.k.st.history.seal();
    return kitKey(this.k, key, (code) => this.repeats(code), (d) => this.arrow(d));
  }

  action(a: MenuAction, modal: Modal | null): void {
    const code = ACTION_KEYS[a];
    if (modal) {
      if (code) modal.key({ code, key: code, shift: false, ctrl: false });
      return;
    }
    if (a === 'up' || a === 'down' || a === 'left' || a === 'right') this.arrow(a);
    else if (code) kitKey(this.k, { code, key: code, shift: false, ctrl: false }, () => false, (d) => this.arrow(d));
  }

  /** One step of an arrow: the cursor in the cave, or the settings form. */
  arrow(d: ArrowDir): void {
    const k = this.k;
    if (k.editing) return;
    if (k.focus === 'form') {
      if (d === 'left' || d === 'right') return FORM_ITEMS[k.formIndex]?.step(k, d === 'left' ? -1 : 1);
      const n = FORM_ITEMS.length;
      k.formIndex = (k.formIndex + (d === 'up' ? n - 1 : 1)) % n;
      k.st.history.seal();
      return k.sound('move');
    }
    const n = this.shift ? 8 : 1;
    const dx = d === 'left' ? -n : d === 'right' ? n : 0;
    const dy = d === 'up' ? -n : d === 'down' ? n : 0;
    k.moveCursor({ x: k.cursor.x + dx, y: k.cursor.y + dy });
  }

  /** A repeating zone (a stepper arrow) was pressed. */
  hold(zone: Zone): void {
    this.held = { zone, ms: 0, wait: HOLD_DELAY_MS };
  }

  /** The pointer moved: leaving a held zone stops its repeat. */
  moved(x: number, y: number): void {
    if (this.held && !inside(this.held.zone, x, y)) this.held = null;
  }

  startPan(x: number, y: number): void {
    this.panAt = { x, y };
  }

  /** Middle drag: scroll with the pointer. True while panning. */
  pan(x: number, y: number): boolean {
    if (!this.panAt) return false;
    const k = this.k;
    k.view = scrollBy(k.view, k.st.doc.grid, VIEW, this.panAt.x - x, this.panAt.y - y);
    this.panAt = { x, y };
    return true;
  }

  up(): void {
    this.held = null;
    this.panAt = null;
  }

  release(): void {
    this.up();
    this.shift = false;
  }

  update(dtMs: number): void {
    const h = this.held;
    if (!h) return;
    h.ms += dtMs;
    h.wait -= dtMs;
    while (h.wait <= 0 && this.held === h) {
      h.wait += h.ms > FAST_AFTER_MS ? 45 : 70;
      h.zone.click?.(0);
    }
  }
}
